/**
 * Timezone Converter View
 * Interactive timezone conversion
 */
import React, { useState } from 'react';
import { Box, Text, useInput } from 'ink';
import TextInput from 'ink-text-input';
import { UtilsService } from '../../../services/utils.service.js';

export const TimezoneView: React.FC = () => {
    const [time, setTime] = useState('');
    const [fromTz, setFromTz] = useState('UTC');
    const [toTz, setToTz] = useState('America/New_York');
    const [result, setResult] = useState('');
    const [error, setError] = useState('');
    const [focusedField, setFocusedField] = useState<'time' | 'from' | 'to' | 'convert'>('time');
    const [copied, setCopied] = useState(false);

    const service = new UtilsService();

    useInput((input, key) => {
        if (key.tab) {
            const fields: typeof focusedField[] = ['time', 'from', 'to', 'convert'];
            const currentIndex = fields.indexOf(focusedField);
            setFocusedField(fields[(currentIndex + 1) % fields.length]);
        } else if (key.return) {
            // Convert on Enter from any field
            handleConvert();
        } else if (input === 'c' && result) {
            handleCopy();
        }
    });

    const handleConvert = () => {
        if (!time) {
            setError('Please enter a time');
            return;
        }
        if (!fromTz || !toTz) {
            setError('Please enter both timezones');
            return;
        }

        try {
            setError('');
            const converted = service.convertTimezone(time, fromTz, toTz);
            setResult(converted);
            setCopied(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Conversion failed');
            setResult('');
        }
    };

    const handleCopy = async () => {
        try {
            const { default: clipboardy } = await import('clipboardy');
            await clipboardy.write(result);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            // Clipboard not available
        }
    };

    return (
        <Box flexDirection="column">
            <Box marginBottom={1}>
                <Text bold color="cyan">
                    🌍 Timezone Converter
                </Text>
            </Box>

            <Box
                flexDirection="column"
                borderStyle="single"
                borderColor="gray"
                paddingX={2}
                paddingY={1}
                marginBottom={1}
            >
                <Text bold color="yellow" marginBottom={1}>
                    Options:
                </Text>

                <Box marginBottom={1} flexDirection="column">
                    <Box marginBottom={0}>
                        <Text color={focusedField === 'time' ? 'green' : undefined}>
                            {focusedField === 'time' ? '▶ ' : '  '}Time:
                        </Text>
                    </Box>
                    <Box marginLeft={2} width={50}>
                        {focusedField === 'time' ? (
                            <TextInput
                                value={time}
                                onChange={setTime}
                                placeholder="2024-01-15 10:00:00"
                            />
                        ) : (
                            <Text dimColor={!time}>{time || '(empty)'}</Text>
                        )}
                    </Box>
                </Box>

                <Box marginBottom={1} flexDirection="column">
                    <Box marginBottom={0}>
                        <Text color={focusedField === 'from' ? 'green' : undefined}>
                            {focusedField === 'from' ? '▶ ' : '  '}From Timezone:
                        </Text>
                    </Box>
                    <Box marginLeft={2} width={50}>
                        {focusedField === 'from' ? (
                            <TextInput
                                value={fromTz}
                                onChange={setFromTz}
                                placeholder="UTC or America/New_York"
                            />
                        ) : (
                            <Text dimColor={!fromTz}>{fromTz || '(empty)'}</Text>
                        )}
                    </Box>
                </Box>

                <Box marginBottom={1} flexDirection="column">
                    <Box marginBottom={0}>
                        <Text color={focusedField === 'to' ? 'green' : undefined}>
                            {focusedField === 'to' ? '▶ ' : '  '}To Timezone:
                        </Text>
                    </Box>
                    <Box marginLeft={2} width={50}>
                        {focusedField === 'to' ? (
                            <TextInput
                                value={toTz}
                                onChange={setToTz}
                                placeholder="Asia/Tokyo or Europe/London"
                            />
                        ) : (
                            <Text dimColor={!toTz}>{toTz || '(empty)'}</Text>
                        )}
                    </Box>
                </Box>

                <Box marginBottom={1}>
                    <Text dimColor>
                        Common: UTC, America/New_York, Europe/London, Asia/Tokyo
                    </Text>
                </Box>

                <Box marginTop={1}>
                    <Text
                        bold
                        backgroundColor={focusedField === 'convert' ? 'green' : undefined}
                        color={focusedField === 'convert' ? 'black' : 'green'}
                    >
                        {focusedField === 'convert' ? '▶ ' : '  '}
                        [ Convert ]
                    </Text>
                </Box>
            </Box>

            {error && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="red"
                    paddingX={2}
                    paddingY={1}
                    marginBottom={1}
                >
                    <Text color="red">✗ Error: {error}</Text>
                </Box>
            )}

            {result && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="green"
                    paddingX={2}
                    paddingY={1}
                >
                    <Box marginBottom={1}>
                        <Text bold color="green">
                            ✓ Result:
                        </Text>
                        {copied && (
                            <Box marginLeft={2}>
                                <Text color="green">✓ Copied to clipboard!</Text>
                            </Box>
                        )}
                    </Box>

                    <Box flexDirection="column">
                        <Text dimColor>From: {fromTz}</Text>
                        <Text dimColor>To: {toTz}</Text>
                        <Box marginTop={1}>
                            <Text wrap="wrap">{result}</Text>
                        </Box>
                    </Box>
                </Box>
            )}

            <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={2}>
                <Text dimColor>
                    Tab: Next | Enter: Convert | C: Copy | Esc: Back
                </Text>
            </Box>
        </Box>
    );
};
